import { AxiosResponse } from 'axios';
import i18next from 'i18next';
import { hideLoading, showLoading } from 'react-redux-loading-bar';
import { replace } from 'react-router-redux';
import { put, select } from 'redux-saga/effects';
import { RootState, store } from 'src/app/store';
import CheckCircleBig from 'src/assets/check-circle-big.svg';
import { routes } from 'src/routes';

import instance, { CustomAxiosError } from '../../axios';
import { apiBaseRoutes, apiRoutes } from '../../constants/apiConstants';
import { Errors } from '../../constants/errorConstants';
import {
  addModal,
  addSnackbar,
  removeModal,
  startLoading,
  stopLoading,
} from '../../features/globalSlice';
import {
  changeParameters,
  getDeviceDetails,
  getParameterDetails,
  setDeviceDetails,
  setParameterDetails,
} from '../actions/deviceActions';
import { Role } from '../models/Auth';
import { Device, IParameter } from '../models/Device';
import { ModalType } from '../models/Modal';
import { SnackbarType } from '../models/Snackbar';
import { ApiResponse } from '.';

/* Get device details */
export function* DeviceGetDetailsSaga(
  action: ReturnType<typeof getDeviceDetails>
): Generator {
  yield put(showLoading());
  if (!action.payload?.refresh) {
    yield put(startLoading({ actionType: action.type }));
  }
  try {
    const state = (yield select()) as RootState;

    const endpoint =
      state?.auth?.user?.role === Role.SERVICE
        ? `${apiBaseRoutes.SERVICE}${apiRoutes.DEVICE}`
        : `${apiBaseRoutes.MANUFACTURER}${apiRoutes.DEVICE}`;

    const response = (yield instance.get(
      `${endpoint}/${action.payload.unitId}`
    )) as AxiosResponse<ApiResponse<Device>>;

    yield put(setDeviceDetails(response.data?.data));
  } catch (e) {
    const error = e as CustomAxiosError;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: error.response?.data?.message || Errors.UNKNOWN_ERROR,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(hideLoading());
    if (!action.payload?.refresh) {
      yield put(stopLoading({ actionType: action.type }));
    }
  }
}

/* Get parameter details */
export function* DeviceGetParameterDetailsSaga(
  action: ReturnType<typeof getParameterDetails>
): Generator {
  yield put(showLoading());
  yield put(startLoading({ actionType: action.type }));
  try {
    const response = (yield instance.get(
      `${apiRoutes.PARAMETERS}`
    )) as AxiosResponse<ApiResponse<{ parameters: IParameter[] }>>;

    yield put(setParameterDetails(response.data?.data?.parameters));
  } catch (e) {
    const error = e as CustomAxiosError;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: error.response?.data?.message || Errors.UNKNOWN_ERROR,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(hideLoading());
    yield put(stopLoading({ actionType: action.type }));
  }
}

/* Change parameters */
export function* DeviceChangeParametersSaga(
  action: ReturnType<typeof changeParameters>
): Generator {
  yield put(showLoading());
  yield put(startLoading({ actionType: action.type }));
  try {
    yield instance.put(
      `${apiRoutes.SERVICE}${apiRoutes.DEVICE}/${action?.payload?.ticket?.unitId}`,
      action?.payload?.parameters
    );

    yield put(
      addModal({
        title: i18next.t('common:success'),
        body: i18next.t('device:new_parameters_written'),
        modalType: ModalType.SUCCESS,
        icon: CheckCircleBig,
        onBackdropClose: false,
        primaryButtonText: i18next.t('device:back_to_ticket_details'),
        primaryAction: () => {
          store.dispatch(removeModal());
          store.dispatch(
            replace(`${routes.TICKETS}/${action?.payload?.ticket?.id}`, {
              forcePush: true,
            })
          );
        },
      })
    );
  } catch (e) {
    const error = e as CustomAxiosError;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: error.response?.data?.message || Errors.UNKNOWN_ERROR,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(hideLoading());
    yield put(stopLoading({ actionType: action.type }));
  }
}
